/**
 * WarmupScreen.js
 * ================
 * Loading screen shown between LandingPage and the first gaze-interaction
 * screen (Quick Chat / HomePage).
 *
 * Purpose
 * ───────
 * The GazeNet Lambda has a cold-start time of 5–15 s (model download from S3).
 * By sending a dummy POST /predict request here, we ensure the Lambda is warm
 * before the user reaches the gaze navigation screens, where a slow first
 * response would be disorienting.
 *
 * Behaviour
 * ─────────
 * 1. Mount → show spinner + "Starting up…" status.
 * 2. Send POST /predict with a 1×1 white JPEG (smallest valid payload).
 * 3. Any response (200 or error) means the Lambda is alive → navigate to /home.
 * 4. Hard timeout at 30 s → navigate anyway so the user is never permanently
 *    blocked by an unresponsive Lambda.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './WarmupScreen.css';

// Maximum time to wait before navigating regardless of Lambda response (ms).
const WARMUP_TIMEOUT_MS = 30_000;

/**
 * Creates a base64-encoded 1×1 white JPEG using a canvas element.
 * This gives us the smallest valid payload for the /predict endpoint.
 * The Lambda may return an inference error for such a tiny image — that is
 * expected and still counts as "Lambda is warm".
 */
function createDummyImageBase64() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 1, 1);
    // Strip the "data:image/jpeg;base64," prefix — API expects raw base64.
    return canvas.toDataURL('image/jpeg', 0.5).split(',')[1];
  } catch {
    // Canvas not available in test environments — return empty string.
    return '';
  }
}

export default function WarmupScreen() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('Starting up…');
  // Prevent double-navigation if both the request and the timeout resolve.
  const navigatedRef = useRef(false);

  useEffect(() => {
    const inferenceUrl = process.env.REACT_APP_INFERENCE_URL;

    function goHome() {
      if (navigatedRef.current) return;
      navigatedRef.current = true;
      navigate('/home', { replace: true });
    }

    // Hard timeout — never leave the user stuck on this screen.
    const timeoutId = setTimeout(() => {
      console.warn('[WarmupScreen] Timeout — navigating without Lambda confirmation.');
      goHome();
    }, WARMUP_TIMEOUT_MS);

    // If no inference URL is configured (e.g. local dev without backend),
    // navigate immediately after a short visual delay.
    if (!inferenceUrl) {
      console.warn('[WarmupScreen] REACT_APP_INFERENCE_URL not set — skipping warmup.');
      setTimeout(goHome, 800);
      clearTimeout(timeoutId);
      return () => clearTimeout(timeoutId);
    }

    // Build the dummy payload.
    const dummyImg = createDummyImageBase64();
    const body = JSON.stringify({
      face:      dummyImg,
      left_eye:  dummyImg,
      right_eye: dummyImg,
    });

    // POST to /predict — any response (including Lambda errors) means it's warm.
    fetch(`${inferenceUrl}/predict`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    })
      .then(() => {
        setStatus('Ready.');
        // Brief pause so the user sees "Ready." before the screen changes.
        setTimeout(goHome, 300);
      })
      .catch(err => {
        // Network error — Lambda may be down. Navigate anyway.
        console.warn('[WarmupScreen] Warmup request failed:', err.message);
        goHome();
      })
      .finally(() => clearTimeout(timeoutId));

    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="warmup-root">
      <p className="warmup-label">Eye2Voice</p>
      <div className="warmup-spinner" aria-label="Loading" role="status" />
      <p className="warmup-status">{status}</p>
    </div>
  );
}
